import React, { useEffect, useState, createContext } from 'react';
import { Routes, Route, useNavigate, Navigate, Outlet } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CircularProgress, Container, Box, Typography } from '@mui/material';

import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import UserRegistry from './pages/UserRegistry';
import Preferences from './pages/Preferences';
import NewSession from './pages/NewSession';
import SessionSelection from './pages/SessionSelection';
import Semester from './pages/Semester';

import { SessionProvider } from './utils/SessionContext';
import Layout from './components/Layout';

export const UserContext = createContext();

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#dc004e', light: '#f73378', dark: '#9a0036' },
    background: { default: '#eff3fb', paper: '#ffffff' },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h4: { fontWeight: 700, letterSpacing: 0.5 },
    h6: { fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          padding: '8px 16px',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
        },
        containedPrimary: {
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 24,
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function ProtectedRoute({ allow, isAuthenticated }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!allow) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

function App() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [facultyProfile, setFacultyProfile] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = userRole === 'Admin';
  const isFaculty = userRole === 'Faculty';
  const isAuthenticated = !!user && !!userRole;

  useEffect(() => {
    const testFirebase = async () => {
      try {
        if (!db) throw new Error('Firestore not initialized');
        const querySnapshot = await getDocs(collection(db, 'Settings'));
        console.log(
          'Firebase connected:',
          querySnapshot.docs.map((doc) => doc.data())
        );
      } catch (error) {
        console.error('Firebase connection error:', error.message);
      }
    };

    testFirebase();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      setUser(currentUser);

      if (!currentUser) {
        setFacultyProfile(null);
        setUserRole(null);
        setLoading(false);
        return;
      }

      try {
        const facultiesRef = collection(db, 'Faculties');

        let facultySnap = await getDocs(
          query(facultiesRef, where('authUid', '==', currentUser.uid), limit(1))
        );

        if (facultySnap.empty && currentUser.email) {
          facultySnap = await getDocs(
            query(
              facultiesRef,
              where('email', '==', currentUser.email.toLowerCase()),
              limit(1)
            )
          );
        }

        if (facultySnap.empty) {
          setFacultyProfile(null);
          setUserRole(null);
          setLoading(false);
          return;
        }

        const facultyDoc = facultySnap.docs[0];
        const facultyData = facultyDoc.data();
        const resolvedRole =
          facultyData.userRole === 'Admin' ? 'Admin' : 'Faculty';

        setFacultyProfile({
          id: facultyDoc.id,
          ...facultyData,
        });
        setUserRole(resolvedRole);
      } catch (error) {
        console.error('Faculty role fetch error:', error.message);
        setFacultyProfile(null);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setFacultyProfile(null);
      setUserRole(null);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error.message);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Container
          maxWidth="lg"
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress color="primary" size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading...
            </Typography>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <UserContext.Provider
        value={{
          user,
          userRole,
          facultyProfile,
          isAdmin,
          isFaculty,
          isAuthenticated,
          logout: handleLogout,
        }}
      >
        <SessionProvider>
          <Routes>
            <Route path="/" element={<SessionSelection />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/newsession/:sessionId" element={<NewSession />} />
            <Route path="/newsession" element={<Navigate to="/" replace />} />

            <Route element={<ProtectedRoute allow={isAdmin || isFaculty} isAuthenticated={isAuthenticated} />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/reports" element={<Reports />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute allow={isAdmin} isAuthenticated={isAuthenticated} />}>
              <Route element={<Layout />}>
                <Route path="/userregistry" element={<UserRegistry />} />
                <Route path="/semester" element={<Semester />} />
                <Route path="/preferences" element={<Preferences />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionProvider>
      </UserContext.Provider>
    </ThemeProvider>
  );
}

export default App;